/**
 * @file screen-template.schema.ts
 * @description As telas padrão do ecossistema — o que uma marca herda
 * enquanto não monta as próprias.
 *
 * ## Por que este arquivo existe
 *
 * Até 9/09/2026 o padrão morava em `globalAppConfig/default`, que era ao mesmo
 * tempo **o layout da marca Eva** e **o layout que todas as outras herdavam**.
 * O `BrandService` herdava "da marca Eva", a marca Eva era sintetizada, e a
 * síntese lia o `globalAppConfig` — o mesmo documento nas duas pontas.
 *
 * O efeito era invisível e errado: mexer na home da Eva mexia na home de toda
 * marca que ainda não personalizara a sua. Ninguém decidiu isso; era
 * consequência de o padrão morar dentro de uma marca.
 *
 * Aqui o padrão vira coisa própria, curada pelo mantenedor. A Eva passa a ser
 * uma herdeira como qualquer outra — e pode ter telas próprias sem que isso
 * mude nada para ninguém.
 *
 * ## Por que um singleton, e não uma marca "template"
 *
 * Uma marca que não é marca vazaria em toda listagem, todo seletor de
 * concessão e toda métrica, exigindo um filtro em cada um. O padrão não tem
 * CNPJ, não tem paciente e não recebe concessão: não é marca.
 */
import { z } from 'zod';
import { AppUiPreferencesSchema, HomeScreenLayoutSchema } from './partner.schema';
import { brandIdField } from './brand';

/** Singleton: `screenTemplates/default`. */
export const SCREEN_TEMPLATE_ID = 'default';

export const ScreenTemplateSchema = z.object({
  id: z.literal(SCREEN_TEMPLATE_ID),

  /** Home herdada por quem não montou a própria. */
  homeScreenLayout: HomeScreenLayoutSchema.optional(),

  /** Tela de Conteúdos herdada. */
  contentScreenLayout: HomeScreenLayoutSchema.optional(),

  /** Tela de Cuidado herdada. */
  careScreenLayout: HomeScreenLayoutSchema.optional(),

  /** Preferências de UI herdadas. */
  appUiPreferences: AppUiPreferencesSchema.optional(),

  /**
   * A marca que a porta base mostra (A044). `app.evasaude.com.br` sem
   * subdomínio é a porta do ecossistema — por onde a mantenedora entra — e
   * também a porta de UMA marca para as pacientes dela. Qual marca é
   * escolha do mantenedor; hoje a Eva, por isso o padrão.
   */
  marcaDaPortaBase: brandIdField(),

  updatedAt: z.coerce.date().optional(),
});

export type ScreenTemplate = z.infer<typeof ScreenTemplateSchema>;
